// API functions for organisation management
// Uses the same apiClient pattern as apiService.ts

import { client as apiClient } from "./api/client.gen";
import type {
  Organisation,
  OrganisationMember,
  OrganisationRole,
  RolePermission,
  MemberStatusFilter,
  PaginationMeta,
  SharingDomainSettings,
  SharingAllowedDomain,
  OrgEmailManagementPolicy,
} from "../types/organisation";
import type { AuthPermission } from "../types";

interface ApiEnvelope<T> {
  data: T;
  correlationId: string;
  eventId: string;
  error: null | { code: string; message: string; breadcrumb: string };
  meta?: Record<string, unknown>;
}

// ============================================================================
// Validation
// ============================================================================

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUUID(value: string, name: string): void {
  if (!UUID_RE.test(value)) {
    throw new Error(`Invalid ${name}: expected UUID`);
  }
}

function unwrap<T>(
  res: { data?: ApiEnvelope<T> | null },
  fallbackMsg: string,
): T {
  if (!res.data || res.data.error) {
    throw new Error(res.data?.error?.message ?? fallbackMsg);
  }
  return res.data.data;
}

// ============================================================================
// Organisation
// ============================================================================

export async function getOrganisation(
  organisationId: string,
): Promise<Organisation> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.get<ApiEnvelope<Organisation>>({
    url: `/v1/organisations/${organisationId}`,
  });
  return unwrap(res, "Failed to fetch organisation");
}

// ============================================================================
// Members
// ============================================================================

export interface ListMembersResult {
  members: OrganisationMember[];
  pagination: PaginationMeta | null;
}

export async function listOrganisationMembers(
  organisationId: string,
  options?: {
    statusFilter?: string;
    pageNumber?: number;
    pageSize?: number;
  },
): Promise<ListMembersResult> {
  assertUUID(organisationId, "organisationId");
  const params = new URLSearchParams();
  if (options?.statusFilter) params.set("status_filter", options.statusFilter);
  if (options?.pageNumber)
    params.set("page_number", String(options.pageNumber));
  if (options?.pageSize) params.set("page_size", String(options.pageSize));
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await apiClient.get<ApiEnvelope<OrganisationMember[]>>({
    url: `/v1/organisations/${organisationId}/members${query}`,
  });
  if (!res.data || res.data.error) {
    throw new Error(res.data?.error?.message ?? "Failed to fetch members");
  }
  const meta = res.data.meta as PaginationMeta | undefined;
  return {
    members: res.data.data,
    pagination: meta ?? null,
  };
}

export async function getMemberStatusFilters(
  organisationId: string,
): Promise<MemberStatusFilter[]> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.get<ApiEnvelope<MemberStatusFilter[]>>({
    url: `/v1/organisations/${organisationId}/member-status-filters`,
  });
  return unwrap(res, "Failed to fetch member status filters");
}

export async function updateOrganisationMember(
  organisationId: string,
  userId: string,
  body: { role_id?: string; status_id?: number },
): Promise<OrganisationMember> {
  assertUUID(organisationId, "organisationId");
  assertUUID(userId, "userId");
  const res = await apiClient.patch<ApiEnvelope<OrganisationMember>>({
    url: `/v1/organisations/${organisationId}/members/${userId}`,
    body: body as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to update member");
}

// ============================================================================
// Roles
// ============================================================================

export async function listOrganisationRoles(
  organisationId: string,
): Promise<OrganisationRole[]> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.get<ApiEnvelope<OrganisationRole[]>>({
    url: `/v1/organisations/${organisationId}/roles`,
  });
  return unwrap(res, "Failed to fetch roles");
}

export async function createOrganisationRole(
  organisationId: string,
  body: { tk: string; slug: string; workos_role_slug?: string },
): Promise<OrganisationRole> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.post<ApiEnvelope<OrganisationRole>>({
    url: `/v1/organisations/${organisationId}/roles`,
    body: body as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to create role");
}

export async function updateOrganisationRole(
  organisationId: string,
  roleId: string,
  body: { tk?: string; workos_role_slug?: string },
): Promise<OrganisationRole> {
  assertUUID(organisationId, "organisationId");
  assertUUID(roleId, "roleId");
  const res = await apiClient.patch<ApiEnvelope<OrganisationRole>>({
    url: `/v1/organisations/${organisationId}/roles/${roleId}`,
    body: body as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to update role");
}

export async function deleteOrganisationRole(
  organisationId: string,
  roleId: string,
): Promise<void> {
  assertUUID(organisationId, "organisationId");
  assertUUID(roleId, "roleId");
  const res = await apiClient.delete<ApiEnvelope<unknown>>({
    url: `/v1/organisations/${organisationId}/roles/${roleId}`,
  });
  unwrap(res, "Failed to delete role");
}

// ============================================================================
// Permissions
// ============================================================================

export async function getRolePermissions(
  organisationId: string,
  roleId: string,
): Promise<RolePermission[]> {
  assertUUID(organisationId, "organisationId");
  assertUUID(roleId, "roleId");
  const res = await apiClient.get<ApiEnvelope<RolePermission[]>>({
    url: `/v1/organisations/${organisationId}/roles/${roleId}/permissions`,
  });
  return unwrap(res, "Failed to fetch permissions");
}

export async function setRolePermissions(
  organisationId: string,
  roleId: string,
  permissions: Array<{ attribute_id: number; has: boolean }>,
): Promise<unknown> {
  assertUUID(organisationId, "organisationId");
  assertUUID(roleId, "roleId");
  const res = await apiClient.put<ApiEnvelope<unknown>>({
    url: `/v1/organisations/${organisationId}/roles/${roleId}/permissions`,
    body: { permissions } as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to save permissions");
}

// ============================================================================
// Sharing Domain Settings
// ============================================================================

export async function getSharingDomainSettings(
  organisationId: string,
): Promise<SharingDomainSettings> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.get<ApiEnvelope<SharingDomainSettings>>({
    url: `/v1/organisations/${organisationId}/sharing-domain-settings`,
  });
  return unwrap(res, "Failed to fetch sharing domain settings");
}

export async function updateSharingDomainRestriction(
  organisationId: string,
  enabled: boolean,
): Promise<SharingDomainSettings> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.patch<ApiEnvelope<SharingDomainSettings>>({
    url: `/v1/organisations/${organisationId}/sharing-domain-settings/restriction`,
    body: { enabled } as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to update sharing domain restriction");
}

export async function addSharingAllowedDomain(
  organisationId: string,
  domain: string,
): Promise<SharingAllowedDomain> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.post<ApiEnvelope<SharingAllowedDomain>>({
    url: `/v1/organisations/${organisationId}/sharing-domain-settings/domains`,
    body: { domain } as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to add sharing allowed domain");
}

export async function removeSharingAllowedDomain(
  organisationId: string,
  domainId: string,
): Promise<void> {
  assertUUID(organisationId, "organisationId");
  assertUUID(domainId, "domainId");
  const res = await apiClient.delete<ApiEnvelope<unknown>>({
    url: `/v1/organisations/${organisationId}/sharing-domain-settings/domains/${domainId}`,
  });
  unwrap(res, "Failed to remove sharing allowed domain");
}

// ============================================================================
// Usage Statistics
// ============================================================================

export interface UsageStats {
  summary: {
    totalWorkflowExecutions: number;
    completedExecutions: number;
    failedExecutions: number;
    totalNodeExecutions: number;
    totalAiApiCalls: number;
    totalAssetsGenerated: number;
  };
  byModel: {
    model: string;
    callCount: number;
    tokenCount: number;
    assetCount: number;
  }[];
  byDay: {
    date: string;
    executions: number;
    nodeExecutions: number;
    aiApiCalls: number;
  }[];
  chartByDay: {
    date: string;
    executions: number;
    nodeExecutions: number;
    aiApiCalls: number;
  }[];
}

export async function getOrganisationUsage(
  organisationId: string,
  periodStart: string,
  periodEnd: string,
  chartStart?: string,
  chartEnd?: string,
): Promise<UsageStats> {
  assertUUID(organisationId, "organisationId");
  const params: Record<string, string> = {
    period_start: periodStart,
    period_end: periodEnd,
  };
  if (chartStart && chartEnd) {
    params.chart_start = chartStart;
    params.chart_end = chartEnd;
  }
  const res = await apiClient.get<ApiEnvelope<UsageStats>>({
    url: `/v1/organisations/${organisationId}/usage?${new URLSearchParams(params).toString()}`,
  });
  return unwrap(res, "Failed to fetch usage statistics");
}

// ============================================================================
// Entitlement Policies
// ============================================================================

export interface EntitlementPolicy {
  id: string;
  scope: "organisation" | "team" | "project";
  organisationId: string | null;
  teamId: string | null;
  projectId: string | null;
  domain: string;
  targetType: string;
  targetEntityId: number;
  targetAttributeKey: string | null;
  targetAttributeValue: string | null;
  policyType: string;
  effect: "deny" | "allow";
  limitValue: number | null;
  period: string;
  windowSeconds: number | null;
  unitTk: string | null;
  nearLimitPct: number;
  counterId: number | null;
  subjectType: string;
  reason: string | null;
  reasonTk: string | null;
  createdBy: string;
  validFrom: string;
  validUntil: string | null;
}

export interface ListPoliciesResult {
  policies: EntitlementPolicy[];
  pagination: PaginationMeta | null;
}

export async function listEntitlementPolicies(
  organisationId: string,
  options?: {
    scope?: string;
    policyType?: string;
    teamId?: string;
    projectId?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<ListPoliciesResult> {
  assertUUID(organisationId, "organisationId");
  const params = new URLSearchParams();
  if (options?.scope) params.set("scope", options.scope);
  if (options?.policyType) params.set("policyType", options.policyType);
  if (options?.teamId) params.set("teamId", options.teamId);
  if (options?.projectId) params.set("projectId", options.projectId);
  if (options?.page) params.set("page", String(options.page));
  if (options?.pageSize) params.set("pageSize", String(options.pageSize));
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await apiClient.get<ApiEnvelope<EntitlementPolicy[]>>({
    url: `/v1/organisations/${organisationId}/entitlement-policies${query}`,
  });
  if (!res.data || res.data.error) {
    throw new Error(
      res.data?.error?.message ?? "Failed to fetch entitlement policies",
    );
  }
  const meta = (res.data.meta as { pagination?: PaginationMeta } | undefined)
    ?.pagination as PaginationMeta | undefined;
  return {
    policies: res.data.data,
    pagination: meta ?? null,
  };
}

export interface CreatePolicyBody {
  scope: "organisation" | "team" | "project";
  teamId?: string;
  projectId?: string;
  domain: string;
  targetType?: string;
  targetEntityId?: number;
  targetAttributeKey?: string;
  targetAttributeValue?: string;
  policyType: string;
  effect?: "deny" | "allow";
  limitValue?: number;
  period?: string;
  windowSeconds?: number;
  unitTk?: string;
  nearLimitPct?: number;
  counterId?: number;
  subjectType?: string;
  reason?: string;
  reasonTk?: string;
  validFrom?: string;
  validUntil?: string;
}

export async function createEntitlementPolicy(
  organisationId: string,
  body: CreatePolicyBody,
): Promise<EntitlementPolicy> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.post<ApiEnvelope<EntitlementPolicy>>({
    url: `/v1/organisations/${organisationId}/entitlement-policies`,
    body: body as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to create entitlement policy");
}

export interface UpdatePolicyBody {
  effect?: "deny" | "allow";
  limitValue?: number;
  period?: string;
  windowSeconds?: number;
  nearLimitPct?: number;
  reason?: string;
  reasonTk?: string;
  validUntil?: string;
}

export async function updateEntitlementPolicy(
  organisationId: string,
  policyId: string,
  body: UpdatePolicyBody,
): Promise<EntitlementPolicy> {
  assertUUID(organisationId, "organisationId");
  assertUUID(policyId, "policyId");
  const res = await apiClient.patch<ApiEnvelope<EntitlementPolicy>>({
    url: `/v1/organisations/${organisationId}/entitlement-policies/${policyId}`,
    body: body as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to update entitlement policy");
}

export async function deleteEntitlementPolicy(
  organisationId: string,
  policyId: string,
): Promise<{ id: string; deleted: boolean }> {
  assertUUID(organisationId, "organisationId");
  assertUUID(policyId, "policyId");
  const res = await apiClient.delete<
    ApiEnvelope<{ id: string; deleted: boolean }>
  >({
    url: `/v1/organisations/${organisationId}/entitlement-policies/${policyId}`,
  });
  return unwrap(res, "Failed to delete entitlement policy");
}

/**
 * Consumer read for entitlement policies applicable to a project.
 *
 * Authorised via project access (not org membership) so team-only external
 * users can read the policies that apply to projects they have access to.
 * See power #228 and docs/plans/entitlement-policy-consumer-read-api.md.
 */
export async function getProjectApplicableEntitlementPolicies(
  projectId: string,
): Promise<{ policies: EntitlementPolicy[] }> {
  assertUUID(projectId, "projectId");
  const res = await apiClient.get<ApiEnvelope<EntitlementPolicy[]>>({
    url: `/v1/projects/${projectId}/applicable-entitlement-policies`,
  });
  if (!res.data || res.data.error) {
    throw new Error(
      res.data?.error?.message ??
        "Failed to fetch applicable entitlement policies",
    );
  }
  return { policies: res.data.data };
}

// ============================================================================
// Email Management Policy
// ============================================================================

export async function getOrgEmailManagementPolicy(
  organisationId: string,
): Promise<OrgEmailManagementPolicy> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.get<ApiEnvelope<OrgEmailManagementPolicy>>({
    url: `/v1/organisations/${organisationId}/email-management-policy`,
  });
  return unwrap(res, "Failed to fetch email management policy");
}

export async function setOrgEmailManagementPolicy(
  organisationId: string,
  enabled: boolean,
): Promise<OrgEmailManagementPolicy> {
  assertUUID(organisationId, "organisationId");
  const res = await apiClient.patch<ApiEnvelope<OrgEmailManagementPolicy>>({
    url: `/v1/organisations/${organisationId}/email-management-policy`,
    body: { enabled } as unknown as Record<string, unknown>,
  });
  return unwrap(res, "Failed to update email management policy");
}

// ============================================================================
// Current User's Organisation Membership
// ============================================================================

export async function getUserOrganisationMembership(): Promise<{
  organisationId: string;
  organisationName: string;
  roleId: string;
  roleSlug: string;
  auth?: Record<string, AuthPermission>;
} | null> {
  const res = await apiClient.get<
    ApiEnvelope<{
      organisationId: string;
      organisationName: string;
      roleId: string;
      roleSlug: string;
      auth?: Record<string, AuthPermission>;
    } | null>
  >({
    url: "/v1/users/me/organisation",
  });
  if (!res.data) return null;
  if (res.data.error) {
    throw new Error(
      res.data.error.message ?? "Failed to fetch organisation membership",
    );
  }
  return res.data.data;
}
